import { Head } from "@unhead/react";

interface ArticleStructuredDataProps {
  title: string;
  description?: string | null;
  author?: string | null;
  datePublished?: string | null;
  dateModified?: string | null;
  image?: string | null;
  url: string;
}

export function ArticleStructuredData({
  title,
  description,
  author,
  datePublished,
  dateModified,
  image,
  url,
}: ArticleStructuredDataProps) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    ...(description && { description }),
    ...(author && {
      author: {
        "@type": "Person",
        name: author,
      },
    }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    ...(image && { image }),
    url,
    publisher: {
      "@type": "Organization",
      name: IS_EXEC ? "Exec" : "Placement",
    },
  };

  return (
    <Head>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Head>
  );
}

interface BreadcrumbStructuredDataProps {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export function BreadcrumbStructuredData({
  items,
}: BreadcrumbStructuredDataProps) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Head>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Head>
  );
}

interface OrganizationStructuredDataProps {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
}

export function OrganizationStructuredData({
  name = IS_EXEC ? "Exec" : "Placement",
  url = IS_EXEC ? "https://www.exec.com" : "https://www.placement.com",
  logo = IS_EXEC
    ? "https://www.exec.com/logo.png"
    : "https://www.placement.com/logo.png",
  description = IS_EXEC
    ? "Exec is the platform for all your talent development programs."
    : "Unlock your potential with career, leadership, and life coaching.",
}: OrganizationStructuredDataProps) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url,
    logo,
    description,
  };

  return (
    <Head>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Head>
  );
}
